import { useCallback, useEffect, useState } from "react";
import { Flame, Trophy, Medal, Award, Building2, Loader2, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { getCondominiosMetricasMensal } from "@/lib/api";
import { fmtInt, fmtPct } from "@/lib/format";

const MEDALS = [
  { icon: Trophy, color: "text-amber-400", bg: "bg-amber-400/15", border: "border-amber-400/40", label: "Ouro" },
  { icon: Medal, color: "text-slate-300", bg: "bg-slate-300/15", border: "border-slate-300/40", label: "Prata" },
  { icon: Award, color: "text-orange-500", bg: "bg-orange-500/15", border: "border-orange-500/40", label: "Bronze" },
];

// Pódio de condomínios com mais clientes vendidos no mês corrente.
// Sinaliza pro vendedor onde há tração — o "prédio quente" pra atacar.
export default function TopCondominiosMesCard({ onNavigate }) {
  const [rows, setRows] = useState([]);
  const [mesAtual, setMesAtual] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getCondominiosMetricasMensal();
      setRows(Array.isArray(r?.top_mes) ? r.top_mes : []);
      setMesAtual(r?.mes_atual || "");
    } catch (e) {
      console.error("Erro top condos mês:", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const mesLabel = mesAtual
    ? new Date(`${mesAtual}-01T00:00:00`).toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
    : "";

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="top-condos-mes-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" />
          <h3 className="font-heading font-semibold text-lg">Condomínios mais quentes do mês</h3>
        </div>
        {mesLabel && (
          <Badge variant="outline" className="text-[10px] capitalize">{mesLabel}</Badge>
        )}
      </div>

      {loading ? (
        <div className="text-center py-6 text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculando…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-6 text-sm text-muted-foreground">
          Ainda não há clientes fechados este mês. Ataque um prédio!
        </div>
      ) : (
        <div className="space-y-2">
          {rows.slice(0, 3).map((r, idx) => {
            const m = MEDALS[idx];
            const Icon = m.icon;
            const dentro = r.pct >= 100;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => onNavigate?.()}
                data-testid={`top-condo-${idx + 1}`}
                className={`w-full rounded-lg border-2 p-3 text-left flex items-center gap-3 transition-all ${m.border} hover:bg-accent`}
              >
                <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${m.bg}`}>
                  <Icon className={`w-5 h-5 ${m.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">{r.condominio}</span>
                    <Badge variant="outline" className="text-[10px]">
                      <Building2 className="w-3 h-3 mr-1" /> #{idx + 1}
                    </Badge>
                    {r.consultor && (
                      <Badge variant="outline" className="text-[10px] text-primary border-primary/40">
                        <User className="w-3 h-3 mr-1" /> {r.consultor}
                      </Badge>
                    )}
                  </div>
                  {r.meta_mensal > 0 && (
                    <div className="mt-1 flex items-center gap-2">
                      <Progress value={Math.min(100, r.pct)} className="h-1.5 flex-1" />
                      <span className={`text-[10px] tabular-nums shrink-0 ${dentro ? "text-emerald-500" : "text-amber-500"}`}>
                        {fmtPct(r.pct)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading text-2xl font-extrabold tabular-nums text-orange-500">
                    {fmtInt(r.clientes_mes)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.meta_mensal > 0 ? `de ${fmtInt(r.meta_mensal)}` : "clientes"}
                  </div>
                </div>
              </button>
            );
          })}
          {rows.length > 3 && (
            <div className="text-[11px] text-muted-foreground text-center pt-1">
              +{rows.length - 3} outros condomínios ativos este mês
            </div>
          )}
        </div>
      )}
    </div>
  );
}
